import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Badge, Spinner, Button, Alert, Dropdown } from 'react-bootstrap';
import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { NoData } from '../../components/SharedComponents/NoData';
import PageTitle from '../../layouts/PageTitle';
import { GlobalFilter } from '../AppsMenu/Invoices/GlobalFilter';
import moment from 'moment';

// Lucide React Icons
import {
  RefreshCw,
  Loader2,
  Upload,
  Landmark,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Link as LinkIcon,
  Wallet,
  Eye,
  FilePlus,
  XCircle,
  FileText,
  MoreVertical,
  MinusCircle,
  CheckCircle2
} from 'lucide-react';

// Import Modal Components
import BankImportModal from './BankImportModal';
import TransactionDetailsModal from './TransactionDetailsModal';
import CreateVerificationFromTransactionModal from './CreateVerificationFromTransactionModal';

export const BankStatementView = () => {
  const { companyId } = useParams();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [accountFilter, setAccountFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateVerificationModal, setShowCreateVerificationModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Helper function to format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    try {
      return moment(dateValue).format('YYYY-MM-DD');
    } catch (error) {
      return dateValue || '-';
    }
  };

  // Fetch bank statements
  const fetchBankData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/statements`),
        { withCredentials: true }
      );

      const statementsData = response.data.statements || [];
      setStatements(statementsData);
    } catch (err) {
      console.error('Error fetching bank statements:', err);
      setError('Det gick inte att hämta banktransaktioner. Försök igen senare.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchBankData();
  };

  // Transaction detail handlers
  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  const handleMatchSuccess = () => {
    setShowDetailsModal(false);
    fetchBankData(); // Refresh data to show updated status
  };

  const handleCreateVerification = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(false);
    setShowCreateVerificationModal(true);
  };

  const handleVerificationSuccess = () => {
    setShowCreateVerificationModal(false);
    fetchBankData(); // Refresh to show updated status
  };

  const handleUnmatch = async (transaction) => {
    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/statements/${transaction.id}/match`
      );
      await axiosInstance.delete(apiUrl, { withCredentials: true });
      fetchBankData(); // Refresh to show updated status
    } catch (err) {
      console.error('Error unmatching transaction:', err);
      setError('Kunde inte ta bort matchning. Försök igen.');
    }
  };

  const handleSkipTransaction = async (transaction) => {
    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/bulk-mark-reconciled`
      );
      await axiosInstance.post(apiUrl, {
        statementIds: [transaction.id],
        status: 'skipped',
        notes: 'Hoppas över av användare'
      }, { withCredentials: true });
      fetchBankData(); // Refresh to show updated status
    } catch (err) {
      console.error('Error skipping transaction:', err);
      setError('Kunde inte hoppa över transaktion. Försök igen.');
    }
  };

  const handleMarkAsReconciled = async (transaction) => {
    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/bulk-mark-reconciled`
      );
      await axiosInstance.post(apiUrl, {
        statementIds: [transaction.id],
        status: 'reconciled_external',
        notes: 'Markerad som redan avstämd av användare'
      }, { withCredentials: true });
      fetchBankData(); // Refresh to show updated status
    } catch (err) {
      console.error('Error marking as reconciled:', err);
      setError('Kunde inte markera som avstämd. Försök igen.');
    }
  };

  useEffect(() => {
    fetchBankData();
  }, [fetchBankData]);

  // Filter statements
  const filteredStatements = useMemo(() => {
    let filtered = statements;

    if (accountFilter !== 'all') {
      filtered = filtered.filter(s => s.account_number === accountFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.reconciliation_status === statusFilter);
    }

    return filtered;
  }, [statements, accountFilter, statusFilter]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const matched = filteredStatements.filter(s =>
      s.reconciliation_status === 'auto_matched' || s.reconciliation_status === 'manual_matched'
    ).length;
    const unmatched = filteredStatements.filter(s => s.reconciliation_status === 'unmatched').length;
    const verified = filteredStatements.filter(s => s.reconciliation_status === 'verified').length;
    const reconciledExternal = filteredStatements.filter(s => s.reconciliation_status === 'reconciled_external').length;
    const skipped = filteredStatements.filter(s => s.reconciliation_status === 'skipped').length;

    const totalInflow = filteredStatements
      .filter(s => parseFloat(s.amount) > 0)
      .reduce((sum, s) => sum + parseFloat(s.amount), 0);

    const totalOutflow = filteredStatements
      .filter(s => parseFloat(s.amount) < 0)
      .reduce((sum, s) => sum + Math.abs(parseFloat(s.amount)), 0);

    const accounts = [...new Set(statements.map(s => s.account_number))];

    // Get current balance from the most recent transaction (by date and account)
    // Group by account and get the latest balance for each
    const balancesByAccount = statements.reduce((acc, s) => {
      const account = s.account_number;
      if (!acc[account] || new Date(s.transaction_date) > new Date(acc[account].transaction_date)) {
        acc[account] = s;
      }
      return acc;
    }, {});

    // Calculate total current balance across all accounts
    const currentBalance = Object.values(balancesByAccount).reduce((sum, s) => {
      return sum + (s.balance ? parseFloat(s.balance) : 0);
    }, 0);

    return {
      totalTransactions: filteredStatements.length,
      matched,
      unmatched,
      verified,
      reconciledExternal,
      skipped,
      totalInflow,
      totalOutflow,
      accounts,
      currentBalance,
      matchRate: filteredStatements.length > 0
        ? Math.round((matched / filteredStatements.length) * 100)
        : 0
    };
  }, [filteredStatements, statements]);

  // Status counts for filter
  const statusCounts = useMemo(() => {
    return filteredStatements.reduce((acc, s) => {
      acc[s.reconciliation_status] = (acc[s.reconciliation_status] || 0) + 1;
      return acc;
    }, {});
  }, [filteredStatements]);

  // Define table columns
  const COLUMNS = useMemo(() => [
    {
      Header: 'Transaktion',
      accessor: 'transaction_date',
      Cell: ({ value, row }) => {
        const isInflow = parseFloat(row.original.amount) > 0;

        return (
          <div className="d-flex align-items-center">
            <div
              className="me-3 d-flex align-items-center justify-content-center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: isInflow ? '#dcfce715' : '#fef2f215',
                color: isInflow ? 'var(--bs-success)' : 'var(--bs-danger)'
              }}
            >
              {isInflow ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#3b82f6' }}>
                  {formatDate(value)}
                </span>
                {row.original.matched_invoice_id && (
                  <div
                    className="badge"
                    style={{
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      fontSize: '0.6875rem'
                    }}
                  >
                    <LinkIcon size={10} className="me-1" />
                    Matchad
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Konto: {row.original.account_number}
                {row.original.reference && ` • Ref: ${row.original.reference}`}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      Header: 'Beskrivning',
      accessor: 'description',
      Cell: ({ value }) => (
        <div>
          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
            {value || '-'}
          </div>
        </div>
      ),
    },
    {
      Header: 'Belopp',
      accessor: 'amount',
      Cell: ({ value }) => {
        const amount = parseFloat(value);
        return (
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: amount >= 0 ? 'var(--bs-success)' : 'var(--bs-danger)'
          }}>
            {new Intl.NumberFormat('sv-SE', {
              style: 'currency',
              currency: 'SEK'
            }).format(amount)}
          </span>
        );
      },
    },
    {
      Header: 'Saldo',
      accessor: 'balance',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
          {value ? new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(value)) : '-'}
        </span>
      ),
    },
    {
      Header: 'Status',
      accessor: 'reconciliation_status',
      Cell: ({ value }) => {
        const statusConfig = {
          'unmatched': { label: 'Omatchad', color: '#6b7280', icon: AlertCircle },
          'auto_matched': { label: 'Auto-matchad', color: '#1e40af', icon: CheckCircle },
          'manual_matched': { label: 'Manuellt matchad', color: '#166534', icon: CheckCircle },
          'verified': { label: 'Verifierad', color: '#15803d', icon: CheckCircle },
          'reconciled_external': { label: 'Redan avstämd', color: '#3b82f6', icon: CheckCircle2 },
          'skipped': { label: 'Överhoppad', color: '#9ca3af', icon: MinusCircle },
        };

        const config = statusConfig[value] || statusConfig['unmatched'];
        const Icon = config.icon;

        return (
          <span
            style={{
              color: config.color,
              fontWeight: '500',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Icon size={14} className="me-1" />
            {config.label}
          </span>
        );
      },
    },
    {
      Header: 'Åtgärder',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const transaction = row.original;
        const isUnmatched = transaction.reconciliation_status === 'unmatched';
        const isMatched = transaction.reconciliation_status === 'auto_matched' ||
                          transaction.reconciliation_status === 'manual_matched';
        const isVerified = transaction.reconciliation_status === 'verified';

        return (
          <Dropdown align="end">
            <Dropdown.Toggle
              size="sm"
              variant="outline-secondary"
              style={{
                fontSize: '0.75rem',
                padding: '0.375rem 0.75rem',
                border: '1px solid #e5e7eb'
              }}
            >
              <MoreVertical size={14} />
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleViewDetails(transaction)}>
                <Eye size={14} className="me-2" />
                Visa detaljer
              </Dropdown.Item>

              {isUnmatched && (
                <>
                  <Dropdown.Item onClick={() => handleViewDetails(transaction)}>
                    <LinkIcon size={14} className="me-2" />
                    Hitta matchningar
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleCreateVerification(transaction)}>
                    <FilePlus size={14} className="me-2" />
                    Skapa verifikat
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleMarkAsReconciled(transaction)}>
                    <CheckCircle2 size={14} className="me-2" />
                    Markera som redan avstämd
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSkipTransaction(transaction)}>
                    <MinusCircle size={14} className="me-2" />
                    Hoppa över
                  </Dropdown.Item>
                </>
              )}

              {(isMatched || isVerified) && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleUnmatch(transaction)}>
                    <XCircle size={14} className="me-2" />
                    Ta bort matchning
                  </Dropdown.Item>
                  {transaction.verification_id && (
                    <Dropdown.Item
                      onClick={() => {
                        // TODO: Navigate to verification view
                        console.log('View verification:', transaction.verification_id);
                      }}
                    >
                      <FileText size={14} className="me-2" />
                      Visa verifikat
                    </Dropdown.Item>
                  )}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ], []);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: filteredStatements,
      initialState: { pageSize: pageSize }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { globalFilter },
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    state: { pageIndex },
  } = tableInstance;

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }

        .clickable-row {
          cursor: pointer;
        }

        .clickable-row:hover {
          background-color: #f8f9fa;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }

        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #3b82f6), var(--accent-color-light, #60a5fa));
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.125rem;
          position: relative;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          color: #1f2937;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          color: #9ca3af;
          font-size: 0.6875rem;
          font-weight: 400;
        }
      `}</style>

      <PageTitle activeMenu="Banktransaktioner" motherMenu="Bank & Skatt" />

      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {statements.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga banktransaktioner att visa. Importera en CSV-fil för att komma igång." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => setShowImportModal(true)}>
                <Upload size={16} className="me-2" />
                Importera banktransaktioner
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Metrics Dashboard */}
            <div className="stats-grid mb-4">
              {/* Current Balance */}
              <div className="stat-card" style={{ '--accent-color': '#3b82f6', '--accent-color-light': '#60a5fa' }}>
                <div className="stat-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
                  <Wallet size={24} />
                </div>
                <div className="stat-value" style={{ color: metrics.currentBalance >= 0 ? 'var(--bs-success)' : 'var(--bs-danger)' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.currentBalance)}
                </div>
                <div className="stat-label">Aktuellt Saldo</div>
                <div className="stat-subtitle">
                  {metrics.accounts.length} bankkonton
                </div>
              </div>

              {/* Inflow */}
              <div className="stat-card" style={{ '--accent-color': 'var(--bs-success)', '--accent-color-light': 'var(--bs-success)' }}>
                <div className="stat-icon" style={{ background: '#f0fdf4', color: 'var(--bs-success)' }}>
                  <TrendingUp size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--bs-success)' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.totalInflow)}
                </div>
                <div className="stat-label">Totala Inbetalningar</div>
                <div className="stat-subtitle">
                  Totalt inflöde
                </div>
              </div>

              {/* Outflow */}
              <div className="stat-card" style={{ '--accent-color': 'var(--bs-danger)', '--accent-color-light': 'var(--bs-danger)' }}>
                <div className="stat-icon" style={{ background: '#fef2f2', color: 'var(--bs-danger)' }}>
                  <TrendingDown size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--bs-danger)' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(metrics.totalOutflow)}
                </div>
                <div className="stat-label">Totala Utbetalningar</div>
                <div className="stat-subtitle">
                  Totalt utflöde
                </div>
              </div>

              {/* Unmatched Transactions */}
              <div className="stat-card" style={{ '--accent-color': '#f59e0b', '--accent-color-light': '#fbbf24' }}>
                <div className="stat-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                  <AlertCircle size={24} />
                </div>
                <div className="stat-value" style={{ color: '#f59e0b' }}>
                  {metrics.unmatched}
                </div>
                <div className="stat-label">Omatchade</div>
                <div className="stat-subtitle">
                  {metrics.totalTransactions} totalt
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Account Filter */}
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>Konto:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{
                      width: 'auto',
                      fontSize: '0.875rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                  >
                    <option value="all">Alla konton ({statements.length})</option>
                    {metrics.accounts.map(account => {
                      const count = statements.filter(s => s.account_number === account).length;
                      return (
                        <option key={account} value={account}>
                          {account} ({count})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>Status:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{
                      width: 'auto',
                      fontSize: '0.875rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Alla ({filteredStatements.length})</option>
                    <option value="unmatched">Omatchade ({statusCounts['unmatched'] || 0})</option>
                    <option value="auto_matched">Auto-matchade ({statusCounts['auto_matched'] || 0})</option>
                    <option value="manual_matched">Manuellt matchade ({statusCounts['manual_matched'] || 0})</option>
                    <option value="verified">Verifierade ({statusCounts['verified'] || 0})</option>
                    <option value="reconciled_external">Redan avstämda ({statusCounts['reconciled_external'] || 0})</option>
                    <option value="skipped">Överhoppade ({statusCounts['skipped'] || 0})</option>
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <RefreshCw size={16} className={refreshing ? "me-1 spin" : "me-1"} />
                  Uppdatera
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowImportModal(true)}
                >
                  <Upload size={16} className="me-2" />
                  Importera CSV
                </Button>
              </div>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar transaktioner...
              </div>
            )}

            {/* Table Card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Search Filter */}
                <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                  <GlobalFilter filter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                      {headerGroups.map((headerGroup, headerGroupIndex) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                          {headerGroup.headers.map((column, columnIndex) => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={`header-${columnIndex}`}
                              style={{
                                padding: '1rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                borderBottom: 'none'
                              }}
                            >
                              {column.render('Header')}
                              <span style={{ marginLeft: '0.25rem' }}>
                                {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                              </span>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row, rowIndex) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()} key={`row-${rowIndex}`} className="clickable-row">
                            {row.cells.map((cell, cellIndex) => (
                              <td
                                {...cell.getCellProps()}
                                key={`cell-${cellIndex}`}
                                style={{ padding: '1rem' }}
                              >
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between px-4 py-3 border-top" style={{ backgroundColor: '#fafbfc' }}>
                  <div className="d-flex align-items-center gap-3">
                    <select
                      className="form-select form-select-sm"
                      style={{
                        width: 'auto',
                        fontSize: '0.875rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setTablePageSize(Number(e.target.value));
                      }}
                    >
                      {[10, 20, 30, 40, 50].map(size => (
                        <option key={size} value={size}>{size} per sida</option>
                      ))}
                    </select>
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      Visar {page.length} av {filteredStatements.length} transaktioner
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => gotoPage(0)}
                      disabled={!canPreviousPage}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      {'<<'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Föregående
                    </Button>
                    <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                      Sida {pageIndex + 1} av {pageOptions.length || 1}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Nästa
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => gotoPage(pageCount - 1)}
                      disabled={!canNextPage}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      {'>>'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Import Modal */}
      <BankImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImportSuccess={() => {
          setShowImportModal(false);
          handleRefresh();
        }}
      />

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        transaction={selectedTransaction}
        onMatchSuccess={handleMatchSuccess}
        onCreateVerification={handleCreateVerification}
      />

      {/* Create Verification Modal */}
      <CreateVerificationFromTransactionModal
        show={showCreateVerificationModal}
        onHide={() => setShowCreateVerificationModal(false)}
        transaction={selectedTransaction}
        onSuccess={handleVerificationSuccess}
      />
    </>
  );
};

export default BankStatementView;
